<template>
    <div
        class="relative shrink-0 overflow-hidden rounded-full outline-none transition-opacity"
        :class="[sizeClasses[size], painted ? 'opacity-100' : 'opacity-0']"
    >
        <img v-if="source" class="h-full w-full" alt="" :src="source" @load="onPainted" @error="onFailed" />
    </div>
</template>

<script lang="ts">
import { createAvatar } from '@dicebear/core';
import * as thumbs from '@dicebear/thumbs';
import { ditherAvatarDataUri } from 'dither-avatar';
import { reactive } from 'vue';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

export type AvatarStyle = 'thumbs' | 'dither';

/* Sources that already painted once this session. The fade is there to hide the
   first load, not to replay every time a table re-renders the same person. */
const seen = new Set<string>();

/* Email -> Gravatar hash, where an empty string means "this address will never have
   one". Reactive and shared so a repeat of a known face resolves without a tick of
   hidden state, and so each address is only ever hashed once. */
const hashes = reactive(new Map<string, string>());
const hashing = new Set<string>();

const resolveHash = (email: string): void => {
    if (!email || hashes.has(email) || hashing.has(email)) return;

    // Undefined outside a secure context, which costs us the Gravatar layer but nothing else.
    if (!window.crypto?.subtle) {
        hashes.set(email, '');

        return;
    }

    hashing.add(email);
    window.crypto.subtle
        .digest('SHA-256', new TextEncoder().encode(email))
        .then((digest) => Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join(''))
        .catch(() => '')
        .then((hash) => hashes.set(email, hash))
        .finally(() => hashing.delete(email));
};

/* Hash -> whether that person actually has a Gravatar, learned from whether the
   image 404s. Keyed on the hash rather than the URL so the answer is shared across
   every size we render them at. */
const gravatars = reactive(new Map<string, boolean>());

/* Both styles are generated locally rather than through api.dicebear.com: no request
   per face and nothing about the person leaves the browser. Only reached for people
   without a Gravatar, so most of these are never built at all. Keyed by style as well
   as seed, since the same person renders differently per tenant. */
const generated = new Map<string, string>();

const generateAvatar = (seed: string, style: AvatarStyle): string => {
    const key = `${style}:${seed}`;
    let avatar = generated.get(key);

    if (!avatar) {
        avatar = style === 'thumbs' ? createAvatar(thumbs, { seed }).toDataUri() : ditherAvatarDataUri(seed);
        generated.set(key, avatar);
    }

    return avatar;
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useSettingsStore } from '~/stores';

/* One avatar scale for the whole panel: xs sits inline in table rows, sm in
   compact cells, md in nav/user-identity spots, lg on profile cards. */
const sizeClasses: Record<AvatarSize, string> = {
    xs: 'h-6 w-6',
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-16 w-16',
};

const sizePixels: Record<AvatarSize, number> = {
    xs: 24,
    sm: 32,
    md: 40,
    lg: 64,
};

const { email, size = 'md' } = defineProps<{
    email?: string;
    size?: AvatarSize;
}>();

const settingsStore = useSettingsStore();

// Absent for panels that have never touched the setting, which are opted in.
const gravatarEnabled = computed(() => settingsStore.data?.misc?.gravatar_enabled !== false);

/* DiceBear thumbs are Physgun's look; every other tenant keeps the dithered avatars.
   Null until settings land, so a cold load can't paint one style and swap to the other. */
const style = computed<AvatarStyle | null>(() => (settingsStore.data ? (settingsStore.isPhysgun ? 'thumbs' : 'dither') : null));

const address = computed(() => email?.trim().toLowerCase() ?? '');

watch([address, gravatarEnabled], () => gravatarEnabled.value && resolveHash(address.value), { immediate: true });

// Undefined only while the address is still being hashed.
const hash = computed(() => (gravatarEnabled.value && address.value ? hashes.get(address.value) : ''));

// d=404 rather than a blank image: a failed request is a definite answer, where a
// transparent PNG is indistinguishable from one that simply hasn't arrived yet.
const gravatar = computed(() => (hash.value ? `https://www.gravatar.com/avatar/${hash.value}?s=${sizePixels[size] * 2}&d=404` : ''));

/* Exactly one image is ever in the DOM. Gravatar gets first refusal, and we only
   fall back once it has actually told us there's nothing there. */
const source = computed(() => {
    if (hash.value === undefined) return '';
    if (gravatar.value && gravatars.get(hash.value) !== false) return gravatar.value;

    return style.value ? generateAvatar(address.value || 'unknown', style.value) : '';
});

const painted = ref(false);

// A source swap starts hidden again unless we have already painted it before.
watch(source, (value) => (painted.value = !!value && seen.has(value)), { immediate: true });

const onPainted = (): void => {
    if (hash.value && source.value === gravatar.value) gravatars.set(hash.value, true);

    seen.add(source.value);
    painted.value = true;
};

const onFailed = (): void => {
    // A 404 means there's no Gravatar account, so hand over to the generated avatar
    // and stay hidden until that one paints.
    if (hash.value && source.value === gravatar.value) {
        gravatars.set(hash.value, false);

        return;
    }

    painted.value = true;
};
</script>
